import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Card, Skeleton, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useCertificateData } from 'src/v3/utilities/hooks/use-certificate-data'
import { CertificatesRollupData, CertificateRollupItem } from '../types'
import {
  computeAccreditationBreakdown,
  computeCategoryBreakdownForType,
  computeSummaryFromRollup,
  filterRollupByDateRange,
} from '../utils/compute-summary'
import { useCertificatesDateFilter } from '../hooks/use-certificates-date-filter'
import ActivityBreakdown from './activity-breakdown'
import CertificatesDateFilter from './certificates-date-filter'

interface LearningOverviewTabProps {
  selectedUserCertificates: CertificatesRollupData | undefined
  isLoading: boolean
  hasError: boolean
  hasLinkedService?: boolean
  downloadHook?: {
    downloadCertificate: (
      item: any,
    ) => Promise<{ success: boolean; error?: string }>
    isDownloading: (id: string | null) => boolean
  }
}

/**
 * Self-contained "Learning Overview" tab content, scoped to whichever user's
 * certificates data is passed in — reused on both the roster user-details
 * page (manager view of a roster user) and the self-service Profile page.
 * All state (date range, drill-down selection, inline grid) is local to
 * this component; it must never read from or write to the "Certificates &
 * Learning Records" tab's grid/filter state on the sibling tab.
 */
export default function LearningOverviewTab({
  selectedUserCertificates,
  isLoading,
  hasError,
  hasLinkedService = false,
  downloadHook,
}: LearningOverviewTabProps): ReactElement {
  let [selectedType, setSelectedType] = useState<
    CertificateRollupItem['type'] | null
  >(null)
  let [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  let dateFilter = useCertificatesDateFilter()

  let filteredRollup = useMemo(
    () =>
      filterRollupByDateRange(
        selectedUserCertificates || [],
        dateFilter.queryParams.startDate,
        dateFilter.queryParams.endDate,
      ),
    [selectedUserCertificates, dateFilter.queryParams],
  )

  let computedSummary = useMemo(
    () => computeSummaryFromRollup(filteredRollup),
    [filteredRollup],
  )

  let categoryBreakdown = useMemo(
    () =>
      selectedType
        ? computeCategoryBreakdownForType(filteredRollup, selectedType)
        : [],
    [filteredRollup, selectedType],
  )

  let accreditationBreakdown = useMemo(
    () => computeAccreditationBreakdown(filteredRollup),
    [filteredRollup],
  )

  // Drop the drill-down selection if the type/category no longer appears
  // in range (e.g. the date filter narrowed it away).
  useEffect(() => {
    if (
      selectedType &&
      !filteredRollup.some((item) => item.type === selectedType)
    ) {
      setSelectedType(null)
    }
  }, [filteredRollup, selectedType])

  useEffect(() => {
    setSelectedCategory(null)
  }, [selectedType])

  let handleSelectType = (type: CertificateRollupItem['type']) => {
    setSelectedType((prev) => (prev === type ? null : type))
  }

  let handleSelectCategory = (categoryName: string) => {
    setSelectedCategory((prev) => (prev === categoryName ? null : categoryName))
  }

  let hasSelection = Boolean(selectedType)

  // Matching rows for the inline results grid — only populated once a
  // segment/category is selected; clearing the selection resets back to
  // just the breakdown, per design.
  let resultsRollup = useMemo(() => {
    if (!selectedType) return []
    return filteredRollup.filter((item) => {
      if (item.type !== selectedType) return false
      if (
        selectedCategory &&
        (item.category || 'Uncategorized') !== selectedCategory
      ) {
        return false
      }
      return true
    })
  }, [filteredRollup, selectedType, selectedCategory])

  let { transformedCertificates, certificateColumns, CustomToolbar } =
    useCertificateData(resultsRollup, isLoading, hasError, downloadHook)

  // A rejected request can leave stale aggregates behind (e.g. the manager
  // flow's reducer retains the previous user's payload on failure), so the
  // error state must take over before any summary/breakdown renders.
  if (hasError) {
    return (
      <Alert severity="error">
        Unable to load certificate data. Try refreshing the page. If the problem
        persists, contact support.
      </Alert>
    )
  }

  let hasDateFilter = Boolean(
    dateFilter.queryParams.startDate || dateFilter.queryParams.endDate,
  )
  let showEmptyState =
    hasDateFilter && !isLoading && filteredRollup.length === 0

  return (
    <Box>
      <CertificatesDateFilter
        startDate={dateFilter.startDate}
        endDate={dateFilter.endDate}
        today={dateFilter.today}
        hasActiveFilter={dateFilter.hasActiveFilter}
        isInvalidRange={dateFilter.isInvalidRange}
        onStartDateChange={dateFilter.updateStartDate}
        onEndDateChange={dateFilter.updateEndDate}
        onClear={dateFilter.clearFilter}
      />

      {showEmptyState ? (
        <Box sx={{ mb: 4, textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No activities found for the selected date range
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting or clearing the date range to see more activities.
          </Typography>
        </Box>
      ) : (
        <ActivityBreakdown
          data={computedSummary}
          isLoading={isLoading}
          hasLinkedService={hasLinkedService}
          selectedType={selectedType}
          onSelectType={handleSelectType}
          categoryBreakdown={categoryBreakdown}
          selectedCategory={selectedCategory}
          onSelectCategory={handleSelectCategory}
          accreditationBreakdown={accreditationBreakdown}
        />
      )}

      {hasSelection && (
        <Card sx={{ mt: 2, p: 3, backgroundColor: 'background.paper' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Matching Records
          </Typography>
          {isLoading ? (
            <Skeleton variant="rectangular" sx={{ height: 400 }} />
          ) : transformedCertificates.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No activities match this selection for the current date range.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 500, width: '100%' }}>
              <DataGrid
                rows={transformedCertificates}
                columns={certificateColumns}
                slots={{ toolbar: CustomToolbar }}
                showToolbar
                loading={isLoading}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                  sorting: {
                    sortModel: [{ field: 'completionDate', sort: 'desc' }],
                  },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
                sx={{
                  backgroundColor: 'background.paper',
                  '& .MuiDataGrid-toolbarContainer': {
                    borderBottom: 1,
                    borderColor: 'divider',
                    p: 2,
                    backgroundColor: 'background.default',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'background.paperDark',
                  },
                }}
              />
            </Box>
          )}
        </Card>
      )}
    </Box>
  )
}
